import cn from 'classnames';
import {useFormikContext} from 'formik';
import {useEffect, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useNavigate} from 'react-router-dom';

import AdvertismentMap from '../../../../../../components/advertisementMap';
import DotsLoading from '../../../../../../components/dotsLoading';
import FormikTextArea from '../../../../../../components/formikFileds/textArea';
import {useAuth} from '../../../../../../context/authContext';
import {RootState} from '../../../../../../store/redux';
import {
  addFormData,
  addRegisterAdvertisement,
  isSendingData,
} from '../../../../../../store/redux/advertisementRegisteration';
import Num2persian from '../../../../../../utilities/helper/convertNumToPersian';
import {ScrollSection} from '../../../../../../utilities/helper/scrollToError/errorScroll.constants';
import {getPriceUnits} from '../../../../../../utilities/helper/unitGenerator';
import {
  WantedRentFarmDurationUnits,
  WantedRentIrrigationDurationUnits,
} from '../../../../../../utilities/helper/unitGenerator/AmountUnits.config';
import {AdvertisementType} from '../../../../../../utilities/helper/unitGenerator/unit.types';
import {quantityPackPropsRent} from '../../../constants';
import {FromikType} from '../../../types';
import {rentHandleDescriptionText} from '../../../utils';
import CommonField from '../../commonField';

const WantedRentFarmForm = ({
  isLoading,
  setModalText,
}: {
  isLoading?: boolean;
  setModalText?: React.Dispatch<React.SetStateAction<string>>;
}) => {
  const {supplierProfile} = useAuth();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const myRef = useRef<HTMLDivElement | null>(null);

  const [touched, setTouched] = useState<boolean>(false);

  const [location, setLocation] = useState<ILocation>({
    lat: Number(supplierProfile?.latlong?.[0]),
    lng: Number(supplierProfile?.latlong?.[1]),
  });
  const [areaText, setAreaText] = useState<string>('');

  const {input, sub_input, input_group, rentBottomSheetData} = useSelector(
    (state: RootState) => state.advertisementRegisteration,
  );
  const {setFieldValue, values, errors} = useFormikContext<FromikType>();

  useEffect(() => {
    if (input_group && sub_input && input_group?.type) {
      setFieldValue(
        'title',
        rentHandleDescriptionText(
          values,
          sub_input?.title,
          values?.wanted_number,
        )[input_group.type],
      );
      setAreaText(
        rentHandleDescriptionText(
          values,
          sub_input?.title,
          values?.wanted_number,
        )[input_group?.type],
      );
    }
  }, [
    values?.wanted_number,
    values?.requirement_during,
    values?.request_date_unit,
    values?.renting_duration_unit,
    values?.renting_duration,
    values?.area,
    values?.area_unit,
  ]);

  useEffect(() => {
    setFieldValue('location', location);
  }, [location]);

  useEffect(() => {
    myRef.current?.scrollIntoView({behavior: 'smooth'});
  }, [myRef.current]);

  const bottomSheetOptions = (item: string) => {
    return {...JSON.parse(item)}.bottomSheetName === 'area_unit'
      ? getPriceUnits(
          rentBottomSheetData as AdvertisementType,
          input_group?.code as number,
          input?.code,
        )
      : input?.code === 11
      ? WantedRentFarmDurationUnits
      : WantedRentIrrigationDurationUnits;
  };

  return (
    <div className="flex flex-col gap-2">
      <section className="flex flex-col" id={ScrollSection.SUB_INPUT}>
        <div className="flex mb-2 text-gray-900 font-Medium" ref={myRef}>
          <span className="text-md">نوع {input?.title} را وارد کنید.</span>
          <span className="color-red-3">*</span>
        </div>
        <div
          className={cn(
            'w-full h-12 rounded border-[1px] border-gray-700 px-2 flex items-center',
            {
              '!border-red-550': touched && errors.sub_input?.title,
              '!border-farm-shop-500':
                values.sub_input?.title && !errors.sub_input?.title,
            },
          )}
        >
          {!sub_input?.title && (
            <i className="mx-1 text-xl text-gray-700 icon-nameSearch" />
          )}
          <input
            className="w-full text-gray-700 text-md"
            name="sub_input"
            readOnly={true}
            value={sub_input?.title}
            placeholder="انتخاب نوع"
            onClick={() => {
              navigate('/advertisement-register-subInput-list', {
                state: {
                  input: input?.guid,
                  backRoute: '/advertisement-registeration',
                },
              });
            }}
          ></input>
        </div>
        <span className="pt-1 text-xs text-red-550">
          {touched && errors.sub_input?.title}
        </span>
      </section>

      {input_group?.code &&
        quantityPackPropsRent[
          (input_group?.code).toString() + input?.code.toString()
        ].map((item, index) => {
          return (
            <div key={index} id={ScrollSection.INPUT_UNIT}>
              <div className="flex mb-2 text-gray-900 font-Medium">
                <span className="text-md">
                  {{...JSON.parse(item)}.titleInput}
                </span>
                {{...JSON.parse(item)}.require && (
                  <span className="color-red-3">*</span>
                )}
              </div>
              <CommonField
                {...JSON.parse(item)}
                touched={touched}
                commonBottomSheetData={bottomSheetOptions(item)}
              />
              {values?.[JSON.parse(item).inputName as keyof FromikType] && (
                <span className="text-xs text-farm-shop-500 pt-[4px] pr-[12px] pb-[0px] pl-[12px]">
                  {/* @ts-ignore */}
                  {`${Num2persian(values?.[JSON.parse(item).inputName])} ${
                    values[
                      JSON.parse(item).bottomSheetName as keyof FromikType
                      /* @ts-ignore */
                    ]?.title
                  }`}
                </span>
              )}
            </div>
          );
        })}
      <section id={ScrollSection.TITLE}>
        <FormikTextArea
          name="title"
          placeholder=""
          label="متن درخواست"
          touched={
            (values.title && !errors.title) || (!values.title && errors.title)
              ? true
              : touched
          }
          required
          rows={5}
          labelClass="!text-md font-medium"
        />
        <span className="text-xs text-gray-900">
          میتوانید متن درخواست خود را ویرایش کنید.
        </span>
      </section>
      <section className="flex flex-col mt-3" id={ScrollSection.LOCATION}>
        <span className="mb-3 text-md">محدوده آگهی</span>
        <div className="z-0 edit-ads-map">
          <AdvertismentMap
            center={location}
            selectLocation={location => {
              setFieldValue('location', location);
              setLocation(location);
            }}
          />
        </div>
      </section>
      <button
        className={cn('w-full text-white h-12 rounded mb-3', {
          'bg-farm-shop-200': isLoading,
          'bg-farm-shop-500': !isLoading,
        })}
        type="submit"
        disabled={isLoading}
        onClick={() => {
          dispatch(
            addRegisterAdvertisement({
              is_change_title: areaText === values?.title,
            }),
          );
          setModalText && setModalText('');
          if (areaText === values?.title) {
            setModalText &&
              setModalText('آگهی شما با موفقیت در فروشگاه مزرعه منتشر شد.');
          }

          dispatch(addFormData(JSON.stringify(values)));
          dispatch(isSendingData(true));
          setTouched(true);
        }}
      >
        {isLoading ? <DotsLoading /> : 'ثبت آگهی'}
      </button>
    </div>
  );
};

export default WantedRentFarmForm;
